import { z } from 'zod';
import { Email, IsoDateTime, LocalDate, PageQuery, Uuid, page, withDefaults } from './common.js';
import { UsageMetric } from './platform.js';
import { TenantRestriction } from './core-p4.js';
import { QueryBoolean } from './public-api-v1.js';
/**
 * Module `billing` (docs/modules/billing.md, ADR 0031, spec 8.6.17). Owners: S41 (plans, subscriptions, invoices, credit
 * notes, payments), S41b (overdue, suspension, export, payment providers). All money is integer pence (GBP).
 */
export const Pence = z.number().int();
export const PositivePence = z.number().int().min(0).max(100_000_000);
export const BillingPeriod = z.enum(['monthly', 'annual']);
// --- Plans (platform) ---------------------------------------------------------------------------------------------------------
export const PlanAllowances = z.object({
    sites: z.number().int().min(0).max(10000).default(1),
    kiosks: z.number().int().min(0).max(10000).default(2),
    smsPerMonth: z.number().int().min(0).max(1_000_000).default(0),
    storageGb: z.number().int().min(0).max(100_000).default(5),
    /** Module keys included in the price. Others enabled for the tenant are charged as add-ons. */
    modules: z.array(z.string()).max(60).default([]),
});
export const PlanExtraPrices = z.object({
    /** Per month, per site or kiosk above the allowance (annual plans: x12 on the annual invoice). */
    perSite: PositivePence.default(0),
    perKiosk: PositivePence.default(0),
    /** SMS above the allowance are charged in bundles. 0 = SMS above the allowance are free. */
    smsBundleSize: z.number().int().min(1).max(1_000_000).default(100),
    perSmsBundle: PositivePence.default(0),
    perStorageGb: PositivePence.default(0),
    /** Monthly price per add-on module key. */
    modules: z.record(z.string(), PositivePence).default({}),
});
export const Plan = z.object({
    id: Uuid,
    code: z.string(),
    name: z.string(),
    monthlyPrice: PositivePence,
    annualPrice: PositivePence,
    allowances: PlanAllowances,
    extras: PlanExtraPrices,
    active: z.boolean(),
    /** Reseller pricing, designed in for later (spec 8.6.1). Always null in Phase 4. */
    partnerId: Uuid.nullable(),
    subscriptionCount: z.number().int(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const PlanCreate = z.object({
    code: z.string().trim().regex(/^[a-z0-9-]{2,40}$/),
    name: z.string().trim().min(1).max(80),
    monthlyPrice: PositivePence,
    annualPrice: PositivePence,
    allowances: withDefaults(PlanAllowances),
    extras: withDefaults(PlanExtraPrices),
});
/** Price changes apply from each subscription's next period; issued invoices never change. */
export const PlanUpdate = z.object({
    name: z.string().trim().min(1).max(80).optional(),
    monthlyPrice: PositivePence.optional(),
    annualPrice: PositivePence.optional(),
    allowances: PlanAllowances.optional(),
    extras: PlanExtraPrices.optional(),
    active: z.boolean().optional(),
});
// --- Subscriptions (one per tenant) -------------------------------------------------------------------------------------------
export const SubscriptionStatus = z.enum(['trial', 'active', 'overdue', 'suspended', 'cancelled']);
export const SubscriptionAddOn = z.object({ moduleKey: z.string(), monthlyPrice: PositivePence });
export const Subscription = z.object({
    id: Uuid,
    tenantId: Uuid,
    planId: Uuid,
    planName: z.string(),
    period: BillingPeriod,
    startDate: LocalDate,
    trialEndsOn: LocalDate.nullable(),
    currentPeriodStart: LocalDate,
    currentPeriodEnd: LocalDate,
    status: SubscriptionStatus,
    addOns: z.array(SubscriptionAddOn),
    /** 0-100, applied to every line of the invoice except credits. */
    discountPercent: z.number().min(0).max(100),
    discountNote: z.string(),
    /** Plan change booked for the next period, if any. */
    pendingPlanId: Uuid.nullable(),
    pendingPeriod: BillingPeriod.nullable(),
    restriction: TenantRestriction,
    overdueBalance: Pence,
    nextInvoiceOn: LocalDate.nullable(),
    cancelledAt: IsoDateTime.nullable(),
    updatedAt: IsoDateTime,
});
export const SubscriptionCreate = z.object({
    planId: Uuid,
    period: BillingPeriod.default('monthly'),
    startDate: LocalDate,
    /** Days of free trial; default from the platform setting. 0 = no trial. */
    trialDays: z.number().int().min(0).max(365).optional(),
    addOns: z.array(SubscriptionAddOn).max(40).default([]),
    discountPercent: z.number().min(0).max(100).default(0),
    discountNote: z.string().trim().max(200).default(''),
});
export const SubscriptionUpdate = z.object({
    addOns: z.array(SubscriptionAddOn).max(40).optional(),
    discountPercent: z.number().min(0).max(100).optional(),
    discountNote: z.string().trim().max(200).optional(),
});
/** Mid-period change: 'now' charges or credits pro rata by days (ADR 0031); 'period_end' books it for the next period. */
export const SubscriptionChangePlan = z.object({
    planId: Uuid,
    period: BillingPeriod,
    effective: z.enum(['now', 'period_end']).default('period_end'),
});
export const SubscriptionCancel = z.object({
    /** 'period_end' keeps it running until the current period ends. */
    effective: z.enum(['now', 'period_end']).default('period_end'),
    reason: z.string().trim().max(500).default(''),
});
// --- Invoices, credit notes and payments ---------------------------------------------------------------------------------------
export const InvoiceKind = z.enum(['invoice', 'credit_note']);
export const InvoiceStatus = z.enum(['draft', 'issued', 'paid', 'credited', 'void_draft']);
export const InvoiceLineKind = z.enum(['plan', 'extra', 'add_on', 'proration', 'discount', 'adjustment', 'credit']);
export const InvoiceLine = z.object({
    kind: InvoiceLineKind,
    description: z.string(),
    quantity: z.number(),
    unitPrice: Pence,
    amount: Pence,
    /** For usage extras: which counter it came from. */
    metric: UsageMetric.nullable(),
});
/** The usage counters frozen into the invoice at the end of the period (ADR 0031): always explains the extras. */
export const UsageSnapshotRow = z.object({ metric: UsageMetric, used: z.number(), allowance: z.number(), charged: z.number() });
export const BillingContactSnapshot = z.object({ name: z.string(), email: z.string(), organisation: z.string(), address: z.string(), purchaseOrder: z.string() });
export const Invoice = z.object({
    id: Uuid,
    tenantId: Uuid,
    tenantName: z.string(),
    kind: InvoiceKind,
    /** Assigned at issue (gapless, `<prefix><year>-<n>`); null on drafts. */
    number: z.string().nullable(),
    creditsInvoiceId: Uuid.nullable(),
    periodStart: LocalDate.nullable(),
    periodEnd: LocalDate.nullable(),
    issuedOn: LocalDate.nullable(),
    dueOn: LocalDate.nullable(),
    status: InvoiceStatus,
    currency: z.literal('GBP'),
    lines: z.array(InvoiceLine),
    subtotal: Pence,
    /** Basis points: 2000 = 20%. */
    vatRateBp: z.number().int(),
    vat: Pence,
    total: Pence,
    amountPaid: Pence,
    amountCredited: Pence,
    balance: Pence,
    usage: z.array(UsageSnapshotRow),
    contact: BillingContactSnapshot,
    pdfAvailable: z.boolean(),
    createdAt: IsoDateTime,
});
export const InvoiceListQuery = PageQuery.extend({
    tenantId: Uuid.optional(),
    status: InvoiceStatus.optional(),
    kind: InvoiceKind.optional(),
    overdue: QueryBoolean.optional(),
});
export const InvoiceList = page(Invoice);
/** Platform: add a manual line to a DRAFT invoice only (issued invoices refuse every change: 409 invoice_issued). */
export const DraftLineAdd = z.object({ description: z.string().trim().min(1).max(200), quantity: z.number().min(0).max(100000).default(1), unitPrice: Pence });
export const CreditNoteCreate = z.object({
    invoiceId: Uuid,
    reason: z.string().trim().min(1).max(500),
    /** Credit the whole remaining amount, or the given lines (positive amounts; stored negative). */
    full: z.boolean().default(false),
    lines: z.array(z.object({ description: z.string().trim().min(1).max(200), amount: PositivePence })).max(20).default([]),
});
export const PaymentMethod = z.enum(['bank_transfer', 'card', 'direct_debit', 'cheque', 'other']);
export const Payment = z.object({
    id: Uuid,
    invoiceId: Uuid,
    amount: Pence,
    paidOn: LocalDate,
    method: PaymentMethod,
    reference: z.string(),
    /** 'manual' until a provider is plugged in (ADR 0031). */
    provider: z.string(),
    recordedByLabel: z.string(),
    createdAt: IsoDateTime,
});
export const PaymentRecord = z.object({
    amount: z.number().int().min(1).max(100_000_000),
    paidOn: LocalDate,
    method: PaymentMethod.default('bank_transfer'),
    reference: z.string().trim().max(120).default(''),
});
/** CSV export for the accounts package (S41b): invoices (one row per invoice) or payments. */
export const BillingExportQuery = z.object({ kind: z.enum(['invoices', 'payments']), from: LocalDate, to: LocalDate });
// --- Tenant billing page and platform console --------------------------------------------------------------------------------
export const AllowanceRow = z.object({ metric: UsageMetric, used: z.number(), allowance: z.number().nullable() });
export const TenantBillingView = z.object({
    subscription: Subscription.nullable(),
    plan: Plan.pick({ id: true, name: true, monthlyPrice: true, annualPrice: true, allowances: true }).nullable(),
    usageThisPeriod: z.array(AllowanceRow),
    invoices: z.array(Invoice.pick({ id: true, number: true, kind: true, issuedOn: true, dueOn: true, status: true, total: true, balance: true, pdfAvailable: true })),
    contact: BillingContactSnapshot,
    /** Provider page to update the card or Direct Debit; null while invoices are paid by hand. */
    paymentMethodUrl: z.string().nullable(),
});
export const PlatformBillingRow = z.object({
    tenantId: Uuid,
    tenantName: z.string(),
    planName: z.string().nullable(),
    status: SubscriptionStatus.nullable(),
    restriction: TenantRestriction,
    nextInvoiceOn: LocalDate.nullable(),
    overdueBalance: Pence,
    oldestOverdueDays: z.number().int(),
});
export const PlatformBillingList = page(PlatformBillingRow);
/** Platform: run the invoice run now for the given day (default today). Idempotent per subscription period. */
export const BillingRunBody = z.object({ date: LocalDate.optional(), /** Run for one organisation only. */ tenantId: Uuid.optional() });
export const BillingRunResult = z.object({ drafts: z.number().int(), issued: z.number().int() });
// --- Settings -------------------------------------------------------------------------------------------------------------------
/** Tenant: the billing contact (edited with billing.manage). */
export const BillingTenantSettings = z.object({
    contactName: z.string().trim().max(120).default(''),
    /** Empty = the tenant's first administrator. */
    contactEmail: z.union([z.literal(''), Email]).default(''),
    organisation: z.string().trim().max(200).default(''),
    address: z.string().trim().max(500).default(''),
    purchaseOrder: z.string().trim().max(60).default(''),
});
/** Platform (Oakford): every number here is a setting (spec 8.6.17 "Settings"). */
export const BillingPlatformSettings = z.object({
    /** UK VAT in basis points (2000 = 20%). */
    vatRateBp: z.number().int().min(0).max(10000).default(2000),
    invoicePrefix: z.string().trim().regex(/^[A-Z0-9-]{0,10}$/).default('CS-'),
    paymentTermsDays: z.number().int().min(0).max(120).default(30),
    /** Reminder emails this many days after the due date. Empty = no reminders. */
    reminderDays: z.array(z.number().int().min(1).max(365)).max(5).default([7, 14]),
    /** Days after the due date before the tenant is restricted. 0 = never. */
    suspendAfterDays: z.number().int().min(0).max(365).default(30),
    /** After suspension, kiosks keep signing people in for this many days; then the portal turns read-only. */
    kioskGraceDays: z.number().int().min(0).max(90).default(14),
    trialDays: z.number().int().min(0).max(365).default(30),
    /** Invoice run: day of month (1-28) monthly invoices are issued for the period just ended. */
    invoiceDay: z.number().int().min(1).max(28).default(1),
    /** 'manual' = invoices marked paid by hand (spec 16 q10). Real providers are added behind the PaymentProvider interface. */
    provider: z.enum(['manual']).default('manual'),
    /** false = the invoice run only makes drafts for Oakford to review; true = the run issues them straight away (S41). */
    autoIssue: z.boolean().default(false),
    seller: withDefaults(z.object({
        name: z.string().max(120).default(''),
        address: z.string().max(500).default(''),
        vatNumber: z.string().max(30).default(''),
        companyNumber: z.string().max(30).default(''),
        bankDetails: z.string().max(300).default(''),
        email: z.union([z.literal(''), Email]).default(''),
    })),
});
//# sourceMappingURL=billing.js.map